import { useEffect, useState } from 'react';

import type { ResponseRow } from '../data/responses.js';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { isModalOpen, isTyping } from '../util/device.js';
import { formatDate } from '../util/format.js';
import { FormAnswers } from './FormAnswersSection.js';
import { idOf } from './responseColumns.js';
import styles from './ResponsePanel.module.css';

/** The context column's slot. The page owns the column; the table draws into it. */
export function ResponsePanelHost({ onMount }: { onMount: (host: HTMLDivElement | null) => void }) {
  return <div ref={onMount} className={styles.host} />;
}

/**
 * One person's answers in full, read beside the table rather than instead of
 * it. `rows` is the table's own order, so stepping always follows what is on
 * screen. Whether the chat still exists is asked once per opened row.
 */
export function ResponsePanel({
  row,
  rows,
  onSelect,
  onOpenChat,
  onChatExists,
}: {
  row: ResponseRow;
  rows: ResponseRow[];
  onSelect: (id: string | null) => void;
  onOpenChat: (cid: string) => void;
  onChatExists: (cid: string) => Promise<boolean>;
}) {
  const { submission } = row;
  const index = rows.findIndex((entry) => idOf(entry) === idOf(row));
  // A response the table has filtered out has no neighbours to step to.
  const previous = index > 0 ? rows[index - 1] : undefined;
  const next = index === -1 ? undefined : rows[index + 1];

  const cid = submission.channelCid;
  const [chatOpen, setChatOpen] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    setChatOpen(null);
    onChatExists(cid)
      .then((exists) => active && setChatOpen(exists))
      // Unknown reads as open: the button is the honest place to find out.
      .catch(() => active && setChatOpen(true));
    return () => {
      active = false;
    };
  }, [cid, onChatExists]);

  useEffect(() => {
    const step = (event: KeyboardEvent) => {
      // A control that already used the key (a grip, the page-size select) keeps it.
      if (event.defaultPrevented || isTyping(event.target) || isModalOpen()) return;
      if ((event.target as HTMLElement | null)?.tagName === 'SELECT') return;
      const target =
        event.key === 'ArrowDown' ? next : event.key === 'ArrowUp' ? previous : undefined;
      if (!target) return;
      event.preventDefault();
      onSelect(idOf(target));
    };
    document.addEventListener('keydown', step);
    return () => document.removeEventListener('keydown', step);
  }, [next, previous, onSelect]);

  return (
    <section className={styles.panel} aria-label="Response">
      <div className={styles.header}>
        <div className={styles.who}>
          <h2 className={styles.title}>{row.person.name ?? 'Jobseeker'}</h2>
          <p className={styles.meta}>
            {[submission.jobTitle, formatDate(submission.response.submittedAt)]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <span className={styles.nav}>
          {index !== -1 && (
            <span className={styles.position}>
              {index + 1} of {rows.length}
            </span>
          )}
          <button
            type="button"
            className={styles.step}
            aria-label="Previous response"
            disabled={!previous}
            onClick={() => previous && onSelect(idOf(previous))}
          >
            <Icon name="chevronUp" size={16} />
          </button>
          <button
            type="button"
            className={styles.step}
            aria-label="Next response"
            disabled={!next}
            onClick={() => next && onSelect(idOf(next))}
          >
            <Icon name="chevronDown" size={16} />
          </button>
          <button
            type="button"
            className={styles.close}
            onClick={() => onSelect(null)}
            aria-label="Close response"
          >
            <Icon name="close" size={18} />
          </button>
        </span>
      </div>

      <div className={styles.body}>
        <FormAnswers fields={submission.notice.fields} answers={submission.response.answers} />
      </div>

      <div className={styles.footer}>
        {chatOpen === false ? (
          <p className={styles.gone}>Chat deleted</p>
        ) : (
          chatOpen === true && <Button onClick={() => onOpenChat(cid)}>Open chat</Button>
        )}
      </div>
    </section>
  );
}
